import type { Point as GeoJsonPoint } from 'geojson';

import { apiClient } from '../client';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type DrillHole = {
  hole_name: string;
  collar_position: GeoJsonPoint;
  toe_position: GeoJsonPoint;
  depth: number;
  bearing: number;
  dip: number;
  rl: number;
  diameter: number;
  redrill: boolean;
  datetime_updated: string; // Timestamp from API for last source update
  pattern_name: string;
  source: string;
  valid: string;
  drill_model: string | null;
  drill_serial: string | null;
};

type DrillHoleQueryParams = {
  // Name of the blast to filter drill holes by
  blast_name: string;
  // Bench height in meters for the Z-level query
  bench_height?: number;
  // Buffer in meters to expand the spatial query bound
  buffer?: number;
  // Additional meters above pattern RL plus bench height for the Z level cutoff
  zabove?: number;
  // Additional meters below pattern RL for the Z level cutoff
  zbelow?: number;
};

type DrillHolesResponse = {
  drill_holes: DrillHole[];
};

/* -------------------------------------------------------------------------- */
/*                                Main service                                */
/* -------------------------------------------------------------------------- */

const drillHoleService = {
  /**
   * Returns a list of drill holes spatially within the blast's bounding box (plus buffer)
   * @param params - Query parameters to filter drill holes by blast name and spatial bounds
   * @return Promise resolving to an object containing an array of drill holes matching the query criteria
   */
  getDrillHoles: async (params: DrillHoleQueryParams): Promise<DrillHolesResponse> => {
    const { blast_name, bench_height, buffer = 20, zabove = 2, zbelow = 2 } = params;
    const response = await apiClient.get<DrillHolesResponse>(`/drill_holes/${blast_name}`, {
      params: { bench_height, buffer, zabove, zbelow, crs: 'local' },
    });
    return response.data;
  },
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { DrillHole, DrillHoleQueryParams, DrillHolesResponse };
export { drillHoleService };
